import { useNavigate } from 'react-router-dom'
import { Wrench } from 'lucide-react'
import type { Account, WorkOrder, WorkOrderStatus } from '@/types/entities'
import { SectionCard, EmptyState, DataTable, Badge, type Column } from '@/components/ui'
import { money, date } from '@/lib/format'
import { propertyById } from '@/lib/selectors'
import { useStore } from '@/store/useStore'
import type { AccountDerived } from '../lib'

const WO_TONE: Record<WorkOrderStatus, 'ok' | 'info' | 'warn' | 'alert' | 'neutral'> = {
  Open: 'warn',
  Dispatched: 'info',
  'In Progress': 'info',
  Completed: 'ok',
  Cancelled: 'neutral',
}

export default function WorkOrdersTab({ d }: { account: Account; d: AccountDerived }) {
  const navigate = useNavigate()
  const data = useStore((s) => s.data)
  const rows = d.workOrders

  if (!rows.length) {
    return (
      <SectionCard pad>
        <EmptyState icon={<Wrench size={36} />} title="No work orders" hint="Operational work orders mirrored from ServiceChannel across this account's properties will appear here." />
      </SectionCard>
    )
  }

  const open = rows.filter((w) => w.status !== 'Completed' && w.status !== 'Cancelled').length
  const totalCost = rows.reduce((s, w) => s + (w.actualCost ?? w.estCost ?? 0), 0)

  const columns: Column<WorkOrder>[] = [
    {
      key: 'number', header: 'WO #', width: 130, sortValue: (w) => w.number,
      render: (w) => <span className="aw-mono">{w.number}</span>,
    },
    {
      key: 'title', header: 'Issue', sortValue: (w) => w.title,
      render: (w) => (
        <div className="aw-cell-stack">
          <span className="aw-wo-title truncate">{w.title}</span>
          {w.trade && <span className="aw-cell-sub">{w.trade}</span>}
        </div>
      ),
    },
    {
      key: 'property', header: 'Property', width: 170,
      sortValue: (w) => propertyById(data, w.propertyId)?.name ?? '',
      render: (w) => {
        const p = propertyById(data, w.propertyId)
        return p ? <span className="aw-link" onClick={(e) => { e.stopPropagation(); navigate(`/properties/${p.id}`) }}>{p.name}</span> : <span className="dim">—</span>
      },
    },
    {
      key: 'vendor', header: 'Vendor', width: 180, sortValue: (w) => w.vendor ?? '',
      render: (w) => <span className="aw-muted-cell">{w.vendor ?? '—'}</span>,
    },
    {
      key: 'status', header: 'Status', width: 120, align: 'center',
      sortValue: (w) => w.status,
      render: (w) => <Badge tone={WO_TONE[w.status]} variant="soft" dot>{w.status}</Badge>,
    },
    {
      key: 'cost', header: 'Cost', width: 110, align: 'right',
      sortValue: (w) => w.actualCost ?? w.estCost ?? -1,
      render: (w) => (
        <span className="num">
          {money(w.actualCost ?? w.estCost)}
          {w.actualCost == null && w.estCost != null && <span className="aw-unit">est</span>}
        </span>
      ),
    },
    {
      key: 'opened', header: 'Opened', width: 100, align: 'right',
      sortValue: (w) => w.openedAt ?? '',
      render: (w) => <span className="num">{date(w.openedAt, 'short')}</span>,
    },
  ]

  return (
    <SectionCard
      eyebrow="Operations"
      title={<>Work <b>Orders</b></>}
      right={<span className="aw-count-pill">{open} open · {money(totalCost, { compact: true })} total</span>}
      pad={false}
    >
      <DataTable<WorkOrder>
        columns={columns}
        rows={rows}
        getRowKey={(w) => w.id}
        initialSort={{ key: 'opened', dir: 'desc' }}
        dense
      />
    </SectionCard>
  )
}
